import { supportsPassiveEvents } from '../utils/feature-detects';

let defaults = {
	start: '0 1',
	end: '1 0',
	onUpdate() {},
	onExitForward() {},
	onExitBackward() {},
	onEnterForward() {},
	onEnterBackward() {},
};

let eventOptions = supportsPassiveEvents() ? { passive: true } : false;

export function ScrollTrigger(element, options) {
	let config = { ...defaults, ...options };
	let { start, end } = config;

	let viewport = document.documentElement;
	let elementRect = getBoundingRect(element);

	let endPoint = getTriggerPoint(end, viewport, element);
	let startPoint = getTriggerPoint(start, viewport, element);
	let entry = getScrollTriggerEntry(getScrollPosition());

	checkScroll();
	window.addEventListener('resize', resize, eventOptions);
	document.addEventListener('scroll', checkScroll, eventOptions);

	function checkScroll() {
		let scrollY = getScrollPosition();

		let {
			isAboveViewport: wasAboveViewport,
			isBelowViewport: wasBelowViewport,
		} = entry;

		let {
			isAboveViewport,
			isBelowViewport,
		} = entry = getScrollTriggerEntry(scrollY);

		if (wasBelowViewport && !isBelowViewport) {
			triggerCallback('onEnterForward');
		}

		if (!wasAboveViewport && isAboveViewport) {
			triggerCallback('onExitForward');
		}

		if (wasAboveViewport && !isAboveViewport) {
			triggerCallback('onEnterBackward');
		}

		if (!wasBelowViewport && isBelowViewport) {
			triggerCallback('onExitBackward');
		}

		if (scrollY >= startPoint.scrollY && scrollY <= endPoint.scrollY) {
			triggerCallback('onUpdate');
		}
	}

	function getScrollPosition() {
		return window.scrollY || document.documentElement.scrollTop;
	}

	function triggerCallback(name) {
		config[name](entry);
	}

	function getScrollTriggerEntry(scrollY) {
		let rect = getVisibleRect();

		return {
			visibleRect: rect,
			isVisible: rect.height > 0,
			isAboveViewport: scrollY > endPoint.scrollY,
			isBelowViewport: scrollY < startPoint.scrollY,
			ratio: rect.height / elementRect.height,
			progress: getProgress(),
		};

		function getProgress() {
			if (scrollY <= startPoint.scrollY) {
				return 0;
			} else if (scrollY >= endPoint.scrollY) {
				return 1;
			} else {
				return (scrollY - startPoint.scrollY) / (endPoint.scrollY - startPoint.scrollY);
			}
		}

		function getVisibleRect() {
			let rect = { top: 0, bottom: 0, left: 0, right: 0, width: 0, height: 0 };

			if (scrollY < startPoint.scrollY || scrollY > endPoint.scrollY) {
				return rect;
			}

			let vT = scrollY;
			let vB = viewport.clientHeight + scrollY;
			let rT = elementRect.topOffset;
			let rB = elementRect.bottomOffset;

			rect.top = Math.max(vT, rT);
			rect.bottom = Math.min(vB, rB);
			rect.left = elementRect.left;
			rect.right = elementRect.right;
			rect.width = elementRect.width;
			rect.height = rect.bottom - rect.top;

			return rect;
		}
	}

	function getBoundingRect(element) {
		let scrollY = getScrollPosition();
		let rect = element.getBoundingClientRect();

		rect['topOffset'] = rect.top + scrollY;
		rect['bottomOffset'] = rect.bottom + scrollY;

		return rect;
	}

	function resize() {
		elementRect = getBoundingRect(element);
		endPoint = getTriggerPoint(end, viewport, element);
		startPoint = getTriggerPoint(start, viewport, element);

		checkScroll();
	}

	function enable() {
		window.addEventListener('resize', resize, eventOptions);
		document.addEventListener('scroll', checkScroll, eventOptions);
	}

	function disable() {
		window.removeEventListener('resize', resize, eventOptions);
		document.removeEventListener('scroll', checkScroll, eventOptions);
	}

	function getTriggerPoint(value, viewport, element) {
		let parts = value.split(' ');

		let viewportHeight = viewport.clientHeight;
		let elementRect = element.getBoundingClientRect();

		let elementTop = elementRect.top + getScrollPosition();
		let elementOffset = elementRect.height * parts[0];
		let viewportOffset = viewportHeight * parts[1];

		return {
			viewport: viewportOffset,
			element: elementTop + elementOffset,
			scrollY: elementTop + elementOffset + (1 - viewportOffset),
		};
	}

	return {
		enable,
		disable,
	};
}
